/**
 * One-shot reads, held briefly and shared by key.
 *
 * The index screens hang a subcollection read off each row — a roster per
 * session on the workspace's list, a job list per finished session on a
 * customer's cards. Neither is on the parent document and there is no cheaper
 * source, so the read itself stays; what this removes is doing it AGAIN for a
 * row that is already on screen. Without it every mount was its own query, and
 * the rows remount constantly: switching the workspace between grid and list,
 * or moving the state filter, unmounts the rows that no longer match and
 * remounts them the moment the filter comes back, at one query each.
 *
 * The promise is what is cached, not the value, so two rows opening in the same
 * tick ask once rather than racing. A rejected read is dropped on the way out,
 * so the next mount genuinely retries instead of inheriting the failure — these
 * back avatar stacks and stat lines, and a refused read shows as the empty
 * state the caller already renders.
 *
 * **Bounded, deliberately**, for the reason `useFacilitatorIndex` spells out at
 * length: something nobody invalidates goes stale, and a roster from ten
 * minutes ago is worse than one extra read. `STALE_AFTER_MS` matches that
 * hook's window on purpose — it is the length of one piece of work, so what
 * goes away is the SECOND read inside a visit, never the refresh on the next
 * one.
 */

const STALE_AFTER_MS = 30_000

interface Held {
  promise: Promise<unknown>
  /** When the read settled, or when it started while it is still out. */
  at: number
}

const held = new Map<string, Held>()

export function cachedRead<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = held.get(key)
  if (existing && Date.now() - existing.at <= STALE_AFTER_MS) {
    return existing.promise as Promise<T>
  }

  const entry: Held = { promise: Promise.resolve(), at: Date.now() }
  entry.promise = load().then(
    (value) => {
      // Measured from the answer, not the question: a slow read must not come
      // back already counted as half stale.
      entry.at = Date.now()
      return value
    },
    (caught: unknown) => {
      if (held.get(key) === entry) held.delete(key)
      throw caught
    }
  )

  /* Set after the read is wired up rather than before, which is safe for the
     same reason the dedupe works at all: nothing is awaited between the lookup
     above and this line, so a second caller in this tick cannot run in the
     gap — it either finds the finished entry or has not started yet. */
  held.set(key, entry)
  return entry.promise as Promise<T>
}
